import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { INVOICES_SLICE_NAME } from "./invoices.constants"
import {
  getInvoiceDownload,
  getInvoiceHistory,
  getInvoices,
  getInvoicesExport,
  getInvoicesExportForUser,
  getInvoicesExportHistory,
  getInvoicesFilterOptions,
  getInvoicesStatusOptions,
  invoiceGenerating,
  reissueInvoice,
  updateInvoice
} from "./invoices.thunks"

/* ------------- Initial State ------------- */

const defaultColumns = [
  { field_id: "date", label: "Date", active: true },
  { field_id: "invoice_amount", label: "Amount", active: true },
  { field_id: "balance_before", label: "Balance Before", active: true },
  { field_id: "balance_after", label: "Balance After", active: true },
  { field_id: "card_last4", label: "Payment Method", active: true },
  { field_id: "paid_with", label: "Paid with", active: true },
  { field_id: "status", label: "Status", active: true },
  { field_id: "topup_type", label: "Type", active: true },
  { field_id: "invoice_id", label: "Transaction ID", active: true }
]

const exceptionalAdminColumns = [{ field_id: "is_reissued", label: "Reissued", active: true }]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumns: [...defaultColumns],
  columnsAdmin: [...defaultColumns, ...exceptionalAdminColumns],

  filtersLastUpd: null,
  filters: {
    date: null,

    status: "all",
    paid_with: "all",
    is_reissued: "all",

    invoice_id: [],
    card_last4: [],

    invoice_amount: { min: null, max: null, type: null },
    balance_before: { min: null, max: null, type: null },
    balance_after: { min: null, max: null, type: null }
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    paid_with: [],
    is_reissued: [],
    topup_type: ["Auto Top up", "Manual Top up"]
  },

  fetchingStatusOptions: false,
  statusOptions: [],

  visibleFilters: { status: true, invoice_amount: true, paid_with: true },
  visibleFiltersAdmin: { status: true, invoice_amount: true, paid_with: true },

  exportFetching: false,
  exportResult: null,
  generateInvoice: null,

  downloadFetching: false,

  invoiceDetails: null,
  invoiceHistoryFetching: false,
  invoiceHistory: {
    count: 0,
    next: null,
    previous: null,
    results: []
  }
}

// Legacy saga dispatched `success(true)` for downloadFile actions, and the reducer treated a
// boolean payload as "no data" (`typeof exportResult === "boolean" ? {} : exportResult`).
const buildExportResult = (type, payload) => ({
  type,
  ...(typeof payload === "boolean" ? {} : payload)
})

const slice = createSlice({
  name: INVOICES_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `invoicesSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `invoicesSetProp({ key, value })`.
    invoicesSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearInvoicesFilters(state) {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- invoices list ---------------- */
      .addCase(getInvoices.pending, (state) => {
        state.fetching = true
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.fetching = false
        const result = action.payload
        state.result = result?.results ? result : { count: result?.length || 0, results: result }
      })
      .addCase(getInvoices.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- reissue invoice ---------------- */
      .addCase(reissueInvoice.pending, (state) => {
        state.fetching = true
      })
      .addCase(reissueInvoice.fulfilled, (state, action) => {
        state.fetching = false
        state.result.count = (state.result?.count || 0) + 1
        state.result.results = [action.payload, ...state.result.results]
      })
      .addCase(reissueInvoice.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- update invoice ---------------- */
      .addCase(updateInvoice.pending, (state) => {
        state.fetching = true
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.fetching = false
        state.result.results = state.result.results.map((el) =>
          el.id === action.payload.id ? action.payload : el
        )
      })
      .addCase(updateInvoice.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- invoice generating ---------------- */
      .addCase(invoiceGenerating.pending, (state) => {
        state.fetching = true
      })
      .addCase(invoiceGenerating.fulfilled, (state, action) => {
        state.fetching = false
        state.generateInvoice = action.payload
      })
      .addCase(invoiceGenerating.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- invoice history (drawer) ---------------- */
      .addCase(getInvoiceHistory.pending, (state) => {
        state.invoiceHistoryFetching = true
      })
      .addCase(getInvoiceHistory.fulfilled, (state, action) => {
        state.invoiceHistoryFetching = false
        const invoiceHistory = action.payload
        state.invoiceHistory = invoiceHistory?.results
          ? invoiceHistory
          : { count: invoiceHistory?.length || 0, results: invoiceHistory }
      })
      .addCase(getInvoiceHistory.rejected, (state) => {
        state.invoiceHistoryFetching = false
      })

      /* ---------------- filter options ---------------- */
      .addCase(getInvoicesFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getInvoicesFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getInvoicesFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- status options ---------------- */
      .addCase(getInvoicesStatusOptions.pending, (state) => {
        state.fetchingStatusOptions = true
      })
      .addCase(getInvoicesStatusOptions.fulfilled, (state, action) => {
        state.fetchingStatusOptions = false
        state.statusOptions = action.payload?.["status_options"] || []
      })
      .addCase(getInvoicesStatusOptions.rejected, (state) => {
        state.fetchingStatusOptions = false
      })

      /* ---------------- export (csv download) ---------------- */
      .addCase(getInvoicesExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getInvoicesExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = buildExportResult("Exporting", action.payload)
      })
      .addCase(getInvoicesExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- export for user ---------------- */
      .addCase(getInvoicesExportForUser.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getInvoicesExportForUser.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = buildExportResult("Exporting", action.payload)
      })
      .addCase(getInvoicesExportForUser.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- export history (invoice drawer) ---------------- */
      .addCase(getInvoicesExportHistory.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getInvoicesExportHistory.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = buildExportResult("Exporting", action.payload)
      })
      .addCase(getInvoicesExportHistory.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- invoice pdf download ---------------- */
      .addCase(getInvoiceDownload.pending, (state) => {
        state.downloadFetching = true
      })
      .addCase(getInvoiceDownload.fulfilled, (state, action) => {
        state.downloadFetching = false
        state.exportResult = buildExportResult("Downloading", action.payload)
      })
      .addCase(getInvoiceDownload.rejected, (state) => {
        state.downloadFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["invoice-history"]?.data
        if (!preference || preference.version !== INVOICE_HISTORY_PREFERENCES_VERSION) return
        if (
          !preference.filters ||
          !preference.columnsAdmin ||
          !preference.visibleFilters ||
          !preference.visibleFiltersAdmin
        )
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFilters = preference.visibleFilters
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const invoicesActions = slice.actions

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.invoices.filters, initialState.filters)

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFilters,
// visibleFiltersAdmin }` payload changes.
export const INVOICE_HISTORY_PREFERENCES_VERSION = 1

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "invoices",
    storage,
    // Only the user's own selections are worth restoring — invoice/result data,
    // filter/status options and export/download state are all re-fetched on mount.
    whitelist: [
      "filters",
      "visibleFilters",
      "visibleFiltersAdmin",
      "defaultColumns",
      "columnsAdmin"
    ],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
